/*
 * Frido order screens — shared runtime (App Proxy client + formatters).
 *
 * Loaded by the order-list and order-details sections. Exposes window.FridoOrders.
 *
 * All order data is read from the Shopify App Proxy at /apps/frido/*. The proxy
 * is what injects logged_in_customer_id (from the storefront customer session),
 * so requests MUST be same-origin and credentialed — calling the upstream service
 * directly would lose both the HMAC signature and the customer identity.
 *
 * Envelope contract (every endpoint): success → { data, status:"SUCCESS", errors:null };
 * failure → { data:null, status:"FAILED", errors:{ error:[{ code, message, ... }] } }.
 * A 401 means the shopper is not logged in (no logged_in_customer_id).
 */
(function () {
  "use strict";

  var PROXY_BASE = "/apps/frido";

  function proxy(path, opts) {
    opts = opts || {};
    var url = new URL(PROXY_BASE + path, window.location.origin);
    if (opts.query) {
      Object.keys(opts.query).forEach(function (k) {
        var v = opts.query[k];
        if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
      });
    }
    return fetch(url.toString(), {
      method: opts.method || "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    }).then(function (res) {
      if (res.status === 401) {
        var notAuthed = new Error("NOT_LOGGED_IN");
        notAuthed.code = "NOT_LOGGED_IN";
        throw notAuthed;
      }
      // Read as text first so a non-JSON body (an HTML error/timeout page from
      // the App Proxy or an upstream gateway) yields a clean error instead of a
      // raw "Unexpected token '<'" — the action may still have completed.
      return res.text().then(function (text) {
        var env;
        try {
          env = JSON.parse(text);
        } catch (parseErr) {
          var bad = new Error("The server returned an unexpected response (status " + res.status + ").");
          bad.code = "BAD_RESPONSE";
          bad.httpStatus = res.status;
          throw bad;
        }
        if (!env || env.status !== "SUCCESS") {
          var first = env && env.errors && env.errors.error && env.errors.error[0];
          // PROVIDER_NOT_CONFIGURED surfaces so callers can degrade (hide Cancel/Track)
          var err = new Error((first && first.message) || "Request failed");
          err.code = (first && first.code) || "UNKNOWN";
          err.internalCode = first && first.internalCode;
          throw err;
        }
        return env.data;
      });
    });
  }

  // ── Formatting ──────────────────────────────────────────────────────────

  function ordinalSuffix(n) {
    var v = n % 100;
    if (v >= 11 && v <= 13) return "th";
    switch (n % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  }

  var MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  // "9th Jun 2026"
  function formatDate(iso) {
    if (!iso) return "";
    var d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    var day = d.getDate();
    return day + ordinalSuffix(day) + " " + MONTHS[d.getMonth()] + " " + d.getFullYear();
  }

  // Money is { amount: "899.00", currencyCode: "INR" }. ₹ for INR, drop .00.
  function money(m) {
    if (!m || m.amount == null) return "";
    var amt = Number(m.amount);
    if (isNaN(amt)) return "";
    var hasPaise = Math.round(amt * 100) % 100 !== 0;
    if (!m.currencyCode || m.currencyCode === "INR") {
      return "₹" + amt.toLocaleString("en-IN", {
        minimumFractionDigits: hasPaise ? 2 : 0,
        maximumFractionDigits: 2,
      });
    }
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: m.currencyCode }).format(amt);
    } catch (e) {
      return m.currencyCode + " " + amt;
    }
  }

  // "10 Jun 2026 03:15 PM" — accepts an ISO string or an epoch (sec or ms).
  function formatDateTime(value) {
    if (value == null || value === "") return "";
    var d;
    var asNum = typeof value === "number" ? value : (/^\d+$/.test(String(value).trim()) ? Number(value) : NaN);
    if (!isNaN(asNum)) d = new Date(asNum < 1e12 ? asNum * 1000 : asNum);
    else d = new Date(value);
    if (isNaN(d.getTime())) return "";
    var h = d.getHours();
    var ampm = h >= 12 ? "PM" : "AM";
    var h12 = h % 12 || 12;
    var pad = function (n) { return n < 10 ? "0" + n : "" + n; };
    return d.getDate() + " " + MONTHS[d.getMonth()] + " " + d.getFullYear() + " " + pad(h12) + ":" + pad(d.getMinutes()) + " " + ampm;
  }

  // Paise (integer) → "₹100" / "₹20,000" / "₹12.50" (en-IN grouped, 2dp only if needed).
  function rupeesFromPaise(paise) {
    if (paise == null) return "";
    var rupees = Number(paise) / 100;
    if (isNaN(rupees)) return "";
    var hasPaise = Math.round(rupees * 100) % 100 !== 0;
    return "₹" + rupees.toLocaleString("en-IN", { minimumFractionDigits: hasPaise ? 2 : 0, maximumFractionDigits: 2 });
  }

  // Always-2-decimals variant for the price breakdown (e.g. ₹949.00).
  function money2(m) {
    if (!m || m.amount == null) return "";
    var amt = Number(m.amount);
    if (isNaN(amt)) return "";
    if (!m.currencyCode || m.currencyCode === "INR") {
      return "₹" + amt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: m.currencyCode }).format(amt);
    } catch (e) {
      return m.currencyCode + " " + amt.toFixed(2);
    }
  }

  function totalQty(lineItems) {
    return (lineItems || []).reduce(function (n, li) {
      return n + (li.quantity || 0);
    }, 0);
  }

  // Authoritative cancelled signal — Shopify sets cancelledAt when an order is
  // cancelled (a Clickpost cancel request flips it once it syncs back).
  function isCancelled(order) {
    if (!order) return false;
    if (order.cancelledAt) return true;
    // Pre-deploy fallback: until the backend returns cancelledAt, treat a VOIDED
    // order as cancelled (the financial status Shopify sets when a Frido order is
    // cancelled). cancelledAt is the authoritative signal once it's deployed.
    return order.displayFinancialStatus === "VOIDED";
  }

  // Order-card / detail-header status label.
  function orderStatusLabel(order) {
    if (!order) return "Order Placed";
    if (isCancelled(order)) return "Order Cancelled";
    if (order.displayFinancialStatus === "REFUNDED") return "Refunded";
    switch (order.displayFulfillmentStatus) {
      case "FULFILLED": return "Shipped";
      case "PARTIALLY_FULFILLED": return "Partially Shipped";
      default: return "Order Placed";
    }
  }

  // Per-delivery label from Shopify FulfillmentDisplayStatus.
  var FULFILLMENT_LABEL = {
    CONFIRMED: "Order Placed",
    IN_PROGRESS: "Processing",
    ON_HOLD: "On Hold",
    SUBMITTED: "Processing",
    OUT_FOR_DELIVERY: "Out for Delivery",
    IN_TRANSIT: "In Transit",
    ATTEMPTED_DELIVERY: "Delivery Attempted",
    DELIVERED: "Delivered",
    CANCELED: "Cancelled",
    FAILURE: "Failed",
  };

  function fulfillmentLabel(displayStatus, order) {
    if (displayStatus && FULFILLMENT_LABEL[displayStatus]) return FULFILLMENT_LABEL[displayStatus];
    return orderStatusLabel(order);
  }

  function escapeHtml(s) {
    if (s == null) return "";
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // ── Copy toast ──────────────────────────────────────────────────────────
  // Small dark pill shown on copy actions across the order screens. Matches
  // Figma node 2639:11263 (bg #252525, 6px radius, green check + white text).
  // Self-contained: injects its own <style> once and appends to <body>, so any
  // order screen that loads this core gets the toast with no extra CSS wiring.
  var TOAST_ICON =
    '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">' +
    '<path d="M9.91667 1.94827C10.7965 2.45627 11.5284 3.18524 12.0399 4.06303C12.5514 4.94081 12.8248 5.93697 12.833 6.95289C12.8412 7.9688 12.5839 8.96924 12.0867 9.85518C11.5894 10.7411 10.8694 11.4818 9.99789 12.004C9.12639 12.5261 8.13363 12.8116 7.11789 12.8321C6.10215 12.8527 5.09865 12.6076 4.20674 12.1212C3.31483 11.6347 2.56542 10.9237 2.03272 10.0587C1.50002 9.19357 1.20249 8.20435 1.16958 7.18894L1.16667 6.99994L1.16958 6.81094C1.20225 5.80351 1.4954 4.82175 2.02046 3.96135C2.54551 3.10095 3.28455 2.39128 4.16552 1.90154C5.04649 1.41179 6.03934 1.15867 7.04725 1.16686C8.05517 1.17505 9.04377 1.44427 9.91667 1.94827ZM9.16242 5.42085C9.06197 5.32042 8.92833 5.26008 8.78656 5.25117C8.6448 5.24225 8.50465 5.28537 8.39242 5.37244L8.33758 5.42085L6.41667 7.34119L5.66242 6.58752L5.60758 6.5391C5.49534 6.4521 5.35522 6.40903 5.21349 6.41798C5.07176 6.42692 4.93815 6.48726 4.83774 6.58767C4.73732 6.68809 4.67698 6.82169 4.66804 6.96342C4.6591 7.10515 4.70217 7.24528 4.78917 7.35752L4.83758 7.41235L6.00425 8.57902L6.05908 8.62744C6.16138 8.70681 6.28719 8.74989 6.41667 8.74989C6.54615 8.74989 6.67195 8.70681 6.77425 8.62744L6.82908 8.57902L9.16242 6.24569L9.21083 6.19085C9.2979 6.07862 9.34102 5.93847 9.3321 5.79671C9.32319 5.65494 9.26285 5.5213 9.16242 5.42085Z" fill="#2EA45E"/>' +
    "</svg>";

  var toastHideTimer = null;

  function ensureToastStyles() {
    if (document.getElementById("fr-toast-styles")) return;
    var style = document.createElement("style");
    style.id = "fr-toast-styles";
    style.textContent =
      ".fr-toast{position:fixed;left:50%;bottom:24px;z-index:2147483000;" +
      "display:inline-flex;align-items:center;justify-content:center;gap:3px;" +
      "padding:8px;border-radius:6px;background:#252525;color:#eaeaea;" +
      "font-family:var(--font-body--family,inherit);font-size:12px;font-weight:600;" +
      "line-height:1;white-space:nowrap;box-shadow:0 4px 16px rgba(0,0,0,0.22);" +
      "opacity:0;transform:translateX(-50%) translateY(8px);" +
      "transition:opacity .18s ease,transform .18s ease;pointer-events:none;}" +
      ".fr-toast.is-visible{opacity:1;transform:translateX(-50%) translateY(0);}" +
      ".fr-toast__ico{display:inline-flex;flex:0 0 auto;}" +
      "@media (prefers-reduced-motion:reduce){.fr-toast{transition:none;}}";
    document.head.appendChild(style);
  }

  // Show a transient toast. `message` is inserted as text (never HTML).
  function toast(message) {
    if (typeof document === "undefined" || !document.body) return;
    ensureToastStyles();
    // Single toast at a time — replace any existing one so rapid copies don't stack.
    var prev = document.querySelector(".fr-toast");
    if (prev && prev.parentNode) prev.parentNode.removeChild(prev);
    if (toastHideTimer) { clearTimeout(toastHideTimer); toastHideTimer = null; }

    var el = document.createElement("div");
    el.className = "fr-toast";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    var ico = document.createElement("span");
    ico.className = "fr-toast__ico";
    ico.setAttribute("aria-hidden", "true");
    ico.innerHTML = TOAST_ICON;
    var txt = document.createElement("span");
    txt.className = "fr-toast__text";
    txt.textContent = message || "Copied";
    el.appendChild(ico);
    el.appendChild(txt);
    document.body.appendChild(el);

    // Force reflow so the enter transition runs from the hidden state.
    void el.offsetWidth;
    el.classList.add("is-visible");

    toastHideTimer = setTimeout(function () {
      el.classList.remove("is-visible");
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 220);
      toastHideTimer = null;
    }, 2000);
  }

  window.FridoOrders = {
    proxy: proxy,
    money: money,
    money2: money2,
    rupeesFromPaise: rupeesFromPaise,
    formatDate: formatDate,
    formatDateTime: formatDateTime,
    totalQty: totalQty,
    orderStatusLabel: orderStatusLabel,
    fulfillmentLabel: fulfillmentLabel,
    isCancelled: isCancelled,
    escapeHtml: escapeHtml,
    toast: toast,
  };
})();
